import {
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    OnInit,
    Output,
    ViewChild,
    AfterViewInit,
    HostListener
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

const INPUT_CONTROL_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => CustomAutosizeInputComponent),
    multi: true
};

@Component({
    selector: 'app-custom-autosize-input',
    providers: [INPUT_CONTROL_ACCESSOR],
    templateUrl: './custom-autosize-input.component.html',
    styleUrls: ['./custom-autosize-input.component.scss']
})
export class CustomAutosizeInputComponent implements ControlValueAccessor, OnInit, AfterViewInit {
    @Input() placeholder = '';
    @Input() value = '';
    @Input() cursorStyle = '';
    @Input() icon?: string;
    @Input() inputStyle: 'bold' | 'normal' = 'normal';
    @Input() required = false;
    @Input() initWithFocus = false;
    @Input() disableNewInstance = false;
    @Input() topIcon?: string;
    @Input() topIconSvg?: string;
    @Input() parenthesis = false;
    @Input() maxLength?: number;
    @Output() valueChange = new EventEmitter<string>();
    @Output() onFocusOutField = new EventEmitter();
    @ViewChild('inputField', { static: false }) inputField!: ElementRef;

    _placeholder = '';
    isNewInstance = true;
    isFocused = false;

    private shouldHandleFocus = false;

    ngOnInit(): void {
        this._placeholder = this.placeholder;
        if (this.disableNewInstance) {
            this.disableIsNewInstance();
        }
        this.shouldHandleFocus = this.initWithFocus;
    }

    ngAfterViewInit(): void {
        if (this.shouldHandleFocus) {
            this.initialFocus();
        }
        this.adjustInputWidth();
    }

    initialFocus(): void {
        if (this.initWithFocus && !this.value) {
            setTimeout(() => {
                this.inputField?.nativeElement?.focus();
                this.shouldHandleFocus = false;
            }, 0);
        }
    }

    onClick(event: Event): void {
        this._placeholder = '';
        this.disableIsNewInstance();
        event.stopPropagation();
    }

    private _onChange?: any;
    private _onTouched?: any;

    onChange(event: Event): void {
        const target = event.target as HTMLInputElement;
        this.value = target.value;
        this.valueChange.emit(this.value);
        this._onChange?.(this.value);
        this._onTouched?.();
        this.adjustInputWidth();
    }

    onFocusOut() {
        this.isFocused = false;
        this._placeholder = this.placeholder;
        this.onFocusOutField.emit();
    }

    onFocusIn() {
        this.isFocused = true;
        if (!this.value) {
            this.value = '';
        }
    }

    registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    writeValue(obj: any): void {
        this.value = obj ?? "";
        setTimeout(() => this.adjustInputWidth(), 0);
    }

    onBlur() {
        if (!this.value.trim()) {
            this.value = '';
        }
        this.inputField?.nativeElement?.blur();
    }

    @HostListener('keydown.enter', ['$event'])
    onEnterPress(event: Event) {
        event.preventDefault();
        this.onBlur();
    }

    private disableIsNewInstance() {
        this.isNewInstance = false;
    }

    private adjustInputWidth() {
        const inputElement = this.inputField?.nativeElement;
        if (!inputElement) return;

        const measurer = document.createElement('span');
        measurer.style.visibility = 'hidden';
        measurer.style.position = 'absolute';
        measurer.style.whiteSpace = 'pre';

        const styles = window.getComputedStyle(inputElement);
        measurer.style.fontFamily = styles.fontFamily;
        measurer.style.fontSize = styles.fontSize;
        measurer.style.fontWeight = styles.fontWeight;
        measurer.style.letterSpacing = styles.letterSpacing;

        // W is used because it is the widest character in most fonts
        measurer.textContent = this.value || this.placeholder || 'W';

        document.body.appendChild(measurer);
        const width = measurer.getBoundingClientRect().width;
        document.body.removeChild(measurer);

        inputElement.style.width = `${Math.max(50, Math.min(400, width + 20))}px`;
    }
}